// Rotas falsas do delegation log (GIY-075; Parte 5.2/5.7; REQ-AUTHZ-006/007/008/010/022), espelhando o servidor:
//   - rotas de estudo: o estudo fora do conjunto acessível ao principal (papéis de estudo de `/me` ou PI cadastrado) → 404 antes de qualquer outra
//     regra (REQ-AUTHZ-011); permissão pelo papel (`rolePermissions`): `delegation.read` lê, `delegation.manage` prepara/emenda, `delegation.sign`
//     assina e revoga; step-up em pi-sign, revoke e accept (401);
//   - rascunho: forma do pedido (422), o PI nunca é delegado do próprio log (422 AUTHZ_SOD_VIOLATION), pessoa desconhecida (422), as travas
//     (423 AUTHZ_DELEGATION_BLOCKED com `blockingConstraints[]` e a tentativa registrada), uma só delegação pendente e uma em curso por pessoa (409);
//   - assinatura do PI: só o PI registrado (422 AUTHZ_DELEGATION_SIGNER_NOT_PI), o hash que ele viu (409 AUTHZ_DELEGATION_CONTENT_CHANGED), travas
//     reavaliadas; aceite só pelo delegado em `/me/delegations`, com SoD (quem assinou não aceita) e o papel de estudo materializado em `/me`;
//   - emenda: versão nova em rascunho, a anterior segue ativa até o aceite (então vira `Superseded`); revogação com razão; exportação em PDF → 423 até o M7.
import type { MeResponse } from '@giyus/api-client';

import type { FakeSessionAuth } from '../auth/FakeAuthProvider';
import { tenantRolePermissions } from '../permissions/rolePermissions';
import { type FakeAuthzState, appendAudit, todayUtc } from './authzStore';
import {
  type DelegationBlockAttemptResponse,
  type FakeDelegation,
  type FakeDelegationLog,
  type FakeDelegationState,
  type FakeStudy,
  CURRENT_STATUSES,
  OPEN_STUDY_STATUSES,
  PENDING_STATUSES,
  ROLE_MAP,
  TASK_TYPES,
  TERMINAL_STATUSES,
  accessibleStudyIds,
  contentHashOf,
  delegationEtag,
  evaluateLocks,
  fakeDelegationId,
  toDelegationResponse,
} from './delegationStore';
import { FakeApiError, jsonResponse, problemResponse, readJsonBody } from './http';
import { type FakePeopleState, displayNameOf } from './peopleStore';

export interface DelegationRouteContext {
  readonly state: FakeDelegationState;
  readonly people: FakePeopleState;
  readonly me: MeResponse;
  readonly correlationId: string | null;
  readonly auth: FakeSessionAuth;
  /** Estado de autorização: recebe os eventos de auditoria. */
  readonly authz?: FakeAuthzState | undefined;
}

interface FieldErrorItem {
  readonly field: string;
  readonly code: string;
  readonly message: string;
}

interface Command {
  readonly personId: string | null;
  readonly functionCode: string | null;
  readonly tasks: readonly string[] | null;
  readonly effectiveFrom: string | null;
  readonly effectiveTo: string | null;
}

const HASH = /^[0-9a-f]{64}$/i;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const MAX_ATTEMPTS = 500;
const MAX_TEXT = 1000;
const LOCALES = new Set(['pt-BR', 'en-US']);
const STATUSES = new Set([
  'Draft',
  'PendingAcceptance',
  'Active',
  'SuspendedCertification',
  'SuspendedLink',
  'Revoked',
  'Expired',
  'Superseded',
]);

// ---- Utilidades ----

function record(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function str(body: unknown, field: string): string | null {
  const value = record(body)[field];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function validation(errors: readonly FieldErrorItem[]): FakeApiError {
  return new FakeApiError(422, 'PLT_VALIDATION', { errors });
}

function fieldError(field: string, code: string): FieldErrorItem {
  return { field, code, message: code };
}

function requireStepUp(ctx: DelegationRouteContext): void {
  if (!ctx.auth.isRecentlyAuthenticated()) {
    throw new FakeApiError(401, 'PLT_STEP_UP_REQUIRED', { error: 'step_up_required' });
  }
}

function ok(body: unknown, etag?: string): Response {
  return jsonResponse(body, 200, etag ? { ETag: etag } : undefined);
}

function created(body: unknown, etag?: string): Response {
  return jsonResponse(body, 201, etag ? { ETag: etag } : undefined);
}

type Params = Readonly<Record<string, string>>;

function match(rest: readonly string[], pattern: readonly string[]): Params | null {
  if (rest.length !== pattern.length) {
    return null;
  }
  const params: Record<string, string> = {};
  for (let index = 0; index < pattern.length; index += 1) {
    const expected = pattern[index] ?? '';
    const actual = rest[index] ?? '';
    if (expected.startsWith(':')) {
      params[expected.slice(1)] = actual;
    } else if (expected !== actual) {
      return null;
    }
  }
  return params;
}

function isIsoDate(value: unknown): value is string {
  if (typeof value !== 'string' || !ISO_DATE.test(value)) {
    return false;
  }
  return new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;
}

// ---- Escopo e permissões ----

/** Estudo da rota: o tenant precisa ser visível, o estudo conhecido, do tenant e acessível ao principal — senão 404 (REQ-AUTHZ-011). */
function requireStudy(
  ctx: DelegationRouteContext,
  tenantId: string,
  studySiteId: string,
): FakeStudy {
  const visible =
    ctx.me.networkAccess.hasAccess ||
    ctx.me.contexts.some((context) => context.tenantId === tenantId);
  const study = ctx.state.studies.find((item) => item.id === studySiteId);
  const reachable =
    visible &&
    study?.tenantId === tenantId &&
    accessibleStudyIds(ctx.me, ctx.state.studies).has(studySiteId);
  if (!study || !reachable) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return study;
}

function studyPermissions(ctx: DelegationRouteContext, study: FakeStudy): Set<string> {
  const result = new Set<string>();
  const context = ctx.me.contexts.find((item) => item.tenantId === study.tenantId);
  for (const effective of context?.effectiveRoles ?? []) {
    const applies =
      effective.scopeType === 'Study'
        ? effective.scopeId === study.id
        : effective.scopeType === 'Tenant' || effective.scopeType === 'Site';
    if (applies) {
      for (const permission of tenantRolePermissions(effective.role)) {
        result.add(permission);
      }
    }
  }
  return result;
}

function need(ctx: DelegationRouteContext, study: FakeStudy, permission: string): void {
  if (!studyPermissions(ctx, study).has(permission)) {
    throw new FakeApiError(403, 'PLT_FORBIDDEN');
  }
}

/** Só o PI registrado assina e revoga (OPEN-112): a pessoa de `/me` precisa ser a cadastrada no estudo. */
function requireRegisteredPi(ctx: DelegationRouteContext, study: FakeStudy): string {
  const personId = ctx.me.person?.id;
  if (!personId || personId !== study.principalInvestigatorPersonId) {
    throw new FakeApiError(422, 'AUTHZ_DELEGATION_SIGNER_NOT_PI');
  }
  return personId;
}

function requireOpen(study: FakeStudy): void {
  if (!OPEN_STUDY_STATUSES.has(study.status)) {
    throw new FakeApiError(409, 'AUTHZ_DELEGATION_STUDY_SITE_NOT_OPEN', { detail: study.status });
  }
}

// ---- Leitura do estado ----

function logOf(ctx: DelegationRouteContext, studySiteId: string): FakeDelegationLog | undefined {
  return ctx.state.logs.find((item) => item.studySiteId === studySiteId);
}

function delegationsOf(ctx: DelegationRouteContext, studySiteId: string): FakeDelegation[] {
  return ctx.state.delegations
    .filter((item) => item.studySiteId === studySiteId)
    .sort(
      (a, b) =>
        b.createdAt.localeCompare(a.createdAt) || b.version - a.version || b.id.localeCompare(a.id),
    );
}

function findDelegation(
  ctx: DelegationRouteContext,
  studySiteId: string,
  delegationId: string,
): FakeDelegation {
  const found = ctx.state.delegations.find(
    (item) => item.id === delegationId && item.studySiteId === studySiteId,
  );
  if (!found) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return found;
}

function respond(ctx: DelegationRouteContext, delegation: FakeDelegation) {
  return toDelegationResponse(delegation, ctx.people, todayUtc());
}

function touch(delegation: FakeDelegation): void {
  delegation.rowVersion += 1;
}

function audit(
  ctx: DelegationRouteContext,
  eventType: string,
  delegation: FakeDelegation,
  after?: unknown,
): void {
  if (!ctx.authz) {
    return;
  }
  const [module] = eventType.split('.');
  appendAudit(ctx.authz, {
    actorUserId: ctx.me.user.id,
    actorDisplayName: ctx.me.user.displayName,
    actorKind: 'user',
    actorRoleSnapshot: null,
    actorLinkSnapshot: null,
    tenantId: delegation.tenantId,
    studySiteId: delegation.studySiteId,
    module: module ?? 'AUTHZ',
    eventType,
    entityType: 'Authorization.Delegation',
    entityId: delegation.id,
    before: null,
    after: after === undefined ? null : JSON.stringify(after),
    changeReasonCode: null,
    changeReasonText: null,
    declaredPurpose: null,
    permission: null,
    correlationId: ctx.correlationId,
  });
}

// ---- Forma do pedido (DelegationRules.Validate) ----

function parseCommand(body: unknown): Command {
  const raw = record(body);
  const tasks = raw['tasks'];
  return {
    personId: str(body, 'personId'),
    functionCode: str(body, 'functionCode'),
    tasks: Array.isArray(tasks)
      ? tasks.map((item) => (typeof item === 'string' ? item : ''))
      : null,
    effectiveFrom: str(body, 'effectiveFrom'),
    effectiveTo: str(body, 'effectiveTo'),
  };
}

function validateCommand(command: Command, today: string): FieldErrorItem[] {
  const errors: FieldErrorItem[] = [];
  if (!command.personId) {
    errors.push(fieldError('personId', 'required'));
  }
  if (!command.functionCode) {
    errors.push(fieldError('functionCode', 'required'));
  } else if (command.functionCode.length > 64) {
    errors.push(fieldError('functionCode', 'too_long'));
  }
  if (!command.tasks || command.tasks.length === 0) {
    errors.push(fieldError('tasks', 'empty'));
  } else {
    if (new Set(command.tasks).size !== command.tasks.length) {
      errors.push(fieldError('tasks', 'duplicate'));
    }
    command.tasks.forEach((code, index) => {
      if (code.trim() === '') {
        errors.push(fieldError(`tasks[${String(index)}]`, 'required'));
      } else if (!TASK_TYPES.some((type) => type.code === code)) {
        errors.push(fieldError(`tasks[${String(index)}]`, 'unknown'));
      } else if (TASK_TYPES.find((type) => type.code === code)?.status !== 'Active') {
        errors.push(fieldError(`tasks[${String(index)}]`, 'retired'));
      }
    });
  }
  if (!command.effectiveFrom) {
    errors.push(fieldError('effectiveFrom', 'required'));
  } else if (!isIsoDate(command.effectiveFrom)) {
    errors.push(fieldError('effectiveFrom', 'invalid'));
  } else {
    if (command.effectiveFrom < today) {
      errors.push(fieldError('effectiveFrom', 'in_past'));
    }
    if (command.effectiveTo && command.effectiveTo < command.effectiveFrom) {
      errors.push(fieldError('effectiveTo', 'before_effective_from'));
    }
  }
  return errors;
}

// ---- Travas ----

/** Julga as travas; com motivos, grava a tentativa (que sobrevive ao 423) e responde `423 AUTHZ_DELEGATION_BLOCKED`. */
function ensureAllowed(
  ctx: DelegationRouteContext,
  study: FakeStudy,
  stage: 'Create' | 'Amend' | 'PiSign' | 'Accept',
  request: {
    readonly personId: string;
    readonly functionCode: string;
    readonly taskCodes: readonly string[];
    readonly effectiveFrom: string;
    readonly effectiveTo: string | null;
    readonly delegationId: string | null;
  },
  attemptedBy: string,
): void {
  const constraints = evaluateLocks(ctx.people, {
    tenantId: study.tenantId,
    studyId: study.studyId,
    personId: request.personId,
    functionCode: request.functionCode,
    taskCodes: request.taskCodes,
    evaluationDate: todayUtc(),
  });
  if (constraints.length === 0) {
    return;
  }
  const person = ctx.people.people.find((item) => item.id === request.personId);
  const visible = person && (person.visibility[study.tenantId]?.length ?? 0) > 0;
  const attempt: DelegationBlockAttemptResponse = {
    id: fakeDelegationId(ctx.state),
    studySiteId: study.id,
    personId: request.personId,
    personDisplayName: person && visible ? displayNameOf(person) : null,
    attemptedBy,
    stage,
    delegationId: request.delegationId,
    functionCode: request.functionCode,
    taskCodes: [...request.taskCodes],
    effectiveFrom: request.effectiveFrom,
    effectiveTo: request.effectiveTo,
    reasons: constraints.map((item) => ({
      code: item.code,
      detail: item.detail,
      certificationTypeCode: item.certificationTypeCode,
      taskCode: item.taskCode,
      councilCode: item.councilCode,
    })),
    occurredAt: new Date().toISOString(),
  };
  ctx.state.attempts.push(attempt);
  throw new FakeApiError(423, 'AUTHZ_DELEGATION_BLOCKED', {
    blockingConstraints: constraints.map((item) => ({
      code: item.code,
      resourceType: item.resourceType,
      resourceId: item.resourceId,
      detail: item.detail,
      ...(item.certificationTypeCode ? { certificationTypeCode: item.certificationTypeCode } : {}),
      ...(item.taskCode ? { taskCode: item.taskCode } : {}),
      ...(item.councilCode ? { councilCode: item.councilCode } : {}),
    })),
  });
}

// ---- Criação de versão (rascunho e emenda) ----

function newDraft(
  ctx: DelegationRouteContext,
  study: FakeStudy,
  command: Command & {
    readonly personId: string;
    readonly functionCode: string;
    readonly tasks: readonly string[];
    readonly effectiveFrom: string;
  },
  previous: FakeDelegation | null,
): FakeDelegation {
  let log = logOf(ctx, study.id);
  if (!log) {
    log = {
      id: fakeDelegationId(ctx.state),
      studySiteId: study.id,
      tenantId: study.tenantId,
      currentVersion: 0,
    };
    ctx.state.logs.push(log);
  }
  log.currentVersion += 1;
  const latest = ctx.state.delegations
    .filter((item) => item.studySiteId === study.id && item.personId === command.personId)
    .sort((a, b) => b.version - a.version)[0];
  const version = (latest?.version ?? 0) + 1;
  const tasks = command.tasks.map((code) => ({
    taskCode: code,
    requiredCertificationTypes: [
      ...(TASK_TYPES.find((type) => type.code === code)?.requiredCertificationTypeCodes ?? []),
    ],
  }));
  const previousDelegationId = previous?.id ?? latest?.id ?? null;
  const account =
    ctx.me.person?.id === command.personId ? ctx.me.user.id : (latest?.userId ?? null);
  const draft: FakeDelegation = {
    id: fakeDelegationId(ctx.state),
    tenantId: study.tenantId,
    studySiteId: study.id,
    delegationLogId: log.id,
    version,
    previousDelegationId,
    personId: command.personId,
    userId: account,
    functionCode: command.functionCode,
    studyRoleCode: ROLE_MAP[command.functionCode] ?? null,
    tasks,
    effectiveFrom: command.effectiveFrom,
    effectiveTo: command.effectiveTo,
    status: 'Draft',
    statusReason: null,
    contentHash: contentHashOf({
      studySiteId: study.id,
      tenantId: study.tenantId,
      version,
      previousDelegationId,
      personId: command.personId,
      functionCode: command.functionCode,
      tasks,
      effectiveFrom: command.effectiveFrom,
      effectiveTo: command.effectiveTo,
    }),
    piSignature: null,
    acceptance: null,
    activatedAt: null,
    endedAt: null,
    endedBy: null,
    roleAssignmentId: null,
    createdBy: ctx.me.user.id,
    createdAt: new Date().toISOString(),
    rowVersion: 1,
  };
  ctx.state.delegations.push(draft);
  return draft;
}

// ---- Papel de estudo materializado em `/me` (só do próprio principal) ----

function grantStudyRole(ctx: DelegationRouteContext, delegation: FakeDelegation): void {
  if (!delegation.studyRoleCode) {
    return;
  }
  const context = ctx.me.contexts.find((item) => item.tenantId === delegation.tenantId);
  if (!context) {
    return;
  }
  const exists = context.effectiveRoles.some(
    (role) =>
      role.scopeType === 'Study' &&
      role.scopeId === delegation.studySiteId &&
      role.role === delegation.studyRoleCode,
  );
  if (!exists) {
    context.effectiveRoles.push({
      scopeType: 'Study',
      scopeId: delegation.studySiteId,
      role: delegation.studyRoleCode,
    });
  }
  context.accessibleStudySiteCount = Math.max(
    Number(context.accessibleStudySiteCount),
    new Set(
      context.effectiveRoles
        .filter((role) => role.scopeType === 'Study')
        .map((role) => role.scopeId),
    ).size,
  );
}

function dropStudyRole(ctx: DelegationRouteContext, delegation: FakeDelegation): void {
  if (!delegation.studyRoleCode) {
    return;
  }
  const context = ctx.me.contexts.find((item) => item.tenantId === delegation.tenantId);
  if (!context) {
    return;
  }
  const stillHeld = ctx.state.delegations.some(
    (item) =>
      item.id !== delegation.id &&
      item.studySiteId === delegation.studySiteId &&
      item.personId === delegation.personId &&
      item.studyRoleCode === delegation.studyRoleCode &&
      CURRENT_STATUSES.has(item.status),
  );
  if (stillHeld) {
    return;
  }
  const index = context.effectiveRoles.findIndex(
    (role) =>
      role.scopeType === 'Study' &&
      role.scopeId === delegation.studySiteId &&
      role.role === delegation.studyRoleCode,
  );
  if (index >= 0) {
    context.effectiveRoles.splice(index, 1);
  }
}

// ---- Rotas de estudo ----

function getLog(ctx: DelegationRouteContext, tenantId: string, studySiteId: string): Response {
  const study = requireStudy(ctx, tenantId, studySiteId);
  need(ctx, study, 'delegation.read');
  const log = logOf(ctx, study.id);
  return ok({
    id: log?.id ?? null,
    studySiteId: study.id,
    principalInvestigatorPersonId: log ? study.principalInvestigatorPersonId : null,
    currentVersion: log?.currentVersion ?? 0,
    delegations: log ? delegationsOf(ctx, study.id).map((item) => respond(ctx, item)) : [],
  });
}

function getTaskTypes(
  ctx: DelegationRouteContext,
  tenantId: string,
  studySiteId: string,
): Response {
  const study = requireStudy(ctx, tenantId, studySiteId);
  need(ctx, study, 'delegation.read');
  return ok([...TASK_TYPES].sort((a, b) => a.code.localeCompare(b.code)));
}

function getDelegation(
  ctx: DelegationRouteContext,
  tenantId: string,
  studySiteId: string,
  delegationId: string,
): Response {
  const study = requireStudy(ctx, tenantId, studySiteId);
  need(ctx, study, 'delegation.read');
  const delegation = findDelegation(ctx, study.id, delegationId);
  return ok(respond(ctx, delegation), delegationEtag(delegation));
}

function getAttempts(ctx: DelegationRouteContext, tenantId: string, studySiteId: string): Response {
  const study = requireStudy(ctx, tenantId, studySiteId);
  need(ctx, study, 'delegation.read');
  const attempts = ctx.state.attempts
    .filter((item) => item.studySiteId === study.id)
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt) || b.id.localeCompare(a.id))
    .slice(0, MAX_ATTEMPTS);
  return ok(attempts);
}

/** `export.pdf`: sem o renderizador do módulo de Documentos (M7), o servidor responde 423 — nunca um PDF de mentira (OPEN-120). */
function exportPdf(
  ctx: DelegationRouteContext,
  request: Request,
  tenantId: string,
  studySiteId: string,
): Response {
  const study = requireStudy(ctx, tenantId, studySiteId);
  need(ctx, study, 'delegation.read');
  const locale = new URL(request.url).searchParams.get('locale');
  if (locale !== null && locale !== '' && !LOCALES.has(locale)) {
    throw validation([fieldError('locale', 'invalid')]);
  }
  throw new FakeApiError(423, 'AUTHZ_DELEGATION_EXPORT_NOT_AVAILABLE', {
    blockingConstraints: [
      {
        code: 'DOCUMENT_TEMPLATE_UNAVAILABLE',
        detail: 'DELEGATION_LOG_PDF_PT (GIY-082/085)',
      },
    ],
  });
}

async function createDraft(
  ctx: DelegationRouteContext,
  request: Request,
  tenantId: string,
  studySiteId: string,
): Promise<Response> {
  const study = requireStudy(ctx, tenantId, studySiteId);
  need(ctx, study, 'delegation.manage');
  requireOpen(study);
  const today = todayUtc();
  const command = parseCommand(await readJsonBody(request));
  const errors = validateCommand(command, today);
  if (errors.length > 0) {
    throw validation(errors);
  }
  const valid = command as Command & {
    personId: string;
    functionCode: string;
    tasks: readonly string[];
    effectiveFrom: string;
  };
  if (study.principalInvestigatorPersonId === valid.personId) {
    throw new FakeApiError(422, 'AUTHZ_SOD_VIOLATION');
  }
  const person = ctx.people.people.find((item) => item.id === valid.personId);
  const known =
    person !== undefined &&
    ((person.visibility[tenantId]?.length ?? 0) > 0 ||
      ctx.people.links.some(
        (link) =>
          link.personId === person.id &&
          (link.tenantId === tenantId || link.tenantId === null) &&
          link.effectiveFrom <= today &&
          (link.effectiveTo === null || link.effectiveTo >= today) &&
          link.endedAt === null,
      ));
  if (!known) {
    throw validation([fieldError('personId', 'unknown')]);
  }
  ensureAllowed(
    ctx,
    study,
    'Create',
    {
      personId: valid.personId,
      functionCode: valid.functionCode,
      taskCodes: valid.tasks,
      effectiveFrom: valid.effectiveFrom,
      effectiveTo: valid.effectiveTo,
      delegationId: null,
    },
    ctx.me.user.id,
  );
  const existing = ctx.state.delegations.filter(
    (item) => item.studySiteId === study.id && item.personId === valid.personId,
  );
  if (existing.some((item) => CURRENT_STATUSES.has(item.status))) {
    throw new FakeApiError(409, 'AUTHZ_DELEGATION_EXISTS', { detail: valid.personId });
  }
  if (existing.some((item) => PENDING_STATUSES.has(item.status))) {
    throw new FakeApiError(409, 'AUTHZ_DELEGATION_VERSION_PENDING', { detail: valid.personId });
  }
  const draft = newDraft(ctx, study, valid, null);
  audit(ctx, 'AUTHZ.DELEGATION_DRAFTED', draft, { version: draft.version, tasks: valid.tasks });
  return created(respond(ctx, draft), delegationEtag(draft));
}

async function amend(
  ctx: DelegationRouteContext,
  request: Request,
  tenantId: string,
  studySiteId: string,
  delegationId: string,
): Promise<Response> {
  const study = requireStudy(ctx, tenantId, studySiteId);
  need(ctx, study, 'delegation.manage');
  const origin = findDelegation(ctx, study.id, delegationId);
  const body = record(await readJsonBody(request));
  const today = todayUtc();
  if (origin.status !== 'Active') {
    throw new FakeApiError(409, 'AUTHZ_DELEGATION_NOT_ACTIVE', { detail: origin.status });
  }
  if (
    ctx.state.delegations.some(
      (item) =>
        item.studySiteId === study.id &&
        item.personId === origin.personId &&
        PENDING_STATUSES.has(item.status),
    )
  ) {
    throw new FakeApiError(409, 'AUTHZ_DELEGATION_VERSION_PENDING', { detail: origin.personId });
  }
  requireOpen(study);
  const tasks = Array.isArray(body['tasks'])
    ? (body['tasks'] as unknown[]).map((item) => (typeof item === 'string' ? item : ''))
    : origin.tasks.map((item) => item.taskCode).sort();
  // A emenda começa hoje (omitido = hoje); um início futuro é `in_future` (o aceite supersede a versão anterior de imediato — sem lacuna de acesso).
  const from = str(body, 'effectiveFrom') ?? today;
  const to =
    body['clearEffectiveTo'] === true ? null : (str(body, 'effectiveTo') ?? origin.effectiveTo);
  const command: Command = {
    personId: origin.personId,
    functionCode: origin.functionCode,
    tasks,
    effectiveFrom: from,
    effectiveTo: to,
  };
  const errors = validateCommand(command, today);
  if (isIsoDate(from) && from > today) {
    errors.push(fieldError('effectiveFrom', 'in_future'));
  }
  if (errors.length > 0) {
    throw validation(errors);
  }
  ensureAllowed(
    ctx,
    study,
    'Amend',
    {
      personId: origin.personId,
      functionCode: origin.functionCode,
      taskCodes: tasks,
      effectiveFrom: from,
      effectiveTo: to,
      delegationId: origin.id,
    },
    ctx.me.user.id,
  );
  const draft = newDraft(
    ctx,
    study,
    {
      ...command,
      personId: origin.personId,
      functionCode: origin.functionCode,
      tasks,
      effectiveFrom: from,
    },
    origin,
  );
  audit(ctx, 'AUTHZ.DELEGATION_AMENDED', draft, { version: draft.version, from: origin.version });
  return created(respond(ctx, draft), delegationEtag(draft));
}

function signedHash(body: unknown): string {
  const hash = str(body, 'contentHash');
  const errors: FieldErrorItem[] = [];
  if (!hash) {
    errors.push(fieldError('contentHash', 'required'));
  } else if (!HASH.test(hash)) {
    errors.push(fieldError('contentHash', 'invalid'));
  }
  const comment = record(body)['comment'];
  if (typeof comment === 'string' && comment.trim().length > MAX_TEXT) {
    errors.push(fieldError('comment', 'too_long'));
  }
  if (errors.length > 0) {
    throw validation(errors);
  }
  return hash ?? '';
}

function periodEnded(delegation: FakeDelegation, today: string): void {
  if (delegation.effectiveTo && delegation.effectiveTo < today) {
    throw new FakeApiError(409, 'AUTHZ_DELEGATION_PERIOD_ENDED', {
      detail: delegation.effectiveTo,
    });
  }
}

async function piSign(
  ctx: DelegationRouteContext,
  request: Request,
  tenantId: string,
  studySiteId: string,
  delegationId: string,
): Promise<Response> {
  const study = requireStudy(ctx, tenantId, studySiteId);
  need(ctx, study, 'delegation.sign');
  requireStepUp(ctx);
  const delegation = findDelegation(ctx, study.id, delegationId);
  const piPerson = requireRegisteredPi(ctx, study);
  const body = await readJsonBody(request);
  const hash = signedHash(body);
  if (delegation.status !== 'Draft') {
    throw new FakeApiError(409, 'AUTHZ_DELEGATION_NOT_DRAFT', { detail: delegation.status });
  }
  requireOpen(study);
  periodEnded(delegation, todayUtc());
  if (hash.toLowerCase() !== delegation.contentHash.toLowerCase()) {
    throw new FakeApiError(409, 'AUTHZ_DELEGATION_CONTENT_CHANGED');
  }
  ensureAllowed(
    ctx,
    study,
    'PiSign',
    {
      personId: delegation.personId,
      functionCode: delegation.functionCode,
      taskCodes: delegation.tasks.map((item) => item.taskCode),
      effectiveFrom: delegation.effectiveFrom,
      effectiveTo: delegation.effectiveTo,
      delegationId: delegation.id,
    },
    ctx.me.user.id,
  );
  delegation.status = 'PendingAcceptance';
  delegation.piSignature = {
    signedBy: ctx.me.user.id,
    signerPersonId: piPerson,
    signedAt: new Date().toISOString(),
    meaning: 'Delegate',
    comment: str(body, 'comment'),
  };
  touch(delegation);
  audit(ctx, 'AUTHZ.DELEGATION_PI_SIGNED', delegation, { contentHash: delegation.contentHash });
  return ok(respond(ctx, delegation), delegationEtag(delegation));
}

async function revoke(
  ctx: DelegationRouteContext,
  request: Request,
  tenantId: string,
  studySiteId: string,
  delegationId: string,
): Promise<Response> {
  const study = requireStudy(ctx, tenantId, studySiteId);
  need(ctx, study, 'delegation.sign');
  requireStepUp(ctx);
  const delegation = findDelegation(ctx, study.id, delegationId);
  requireRegisteredPi(ctx, study);
  const reason = str(await readJsonBody(request), 'reason');
  if (!reason) {
    throw validation([fieldError('reason', 'required')]);
  }
  if (reason.length > MAX_TEXT) {
    throw validation([fieldError('reason', 'too_long')]);
  }
  if (TERMINAL_STATUSES.has(delegation.status)) {
    throw new FakeApiError(409, 'AUTHZ_DELEGATION_ALREADY_ENDED', { detail: delegation.status });
  }
  const wasCurrent = CURRENT_STATUSES.has(delegation.status);
  delegation.status = 'Revoked';
  delegation.statusReason = reason;
  delegation.endedAt = new Date().toISOString();
  delegation.endedBy = ctx.me.user.id;
  touch(delegation);
  const log = logOf(ctx, study.id);
  if (log && wasCurrent) {
    log.currentVersion += 1;
  }
  if (wasCurrent && delegation.userId === ctx.me.user.id) {
    dropStudyRole(ctx, delegation);
  }
  audit(ctx, 'AUTHZ.DELEGATION_REVOKED', delegation, { reason });
  return ok(respond(ctx, delegation), delegationEtag(delegation));
}

// ---- Rotas da própria pessoa ----

function isMine(ctx: DelegationRouteContext, delegation: FakeDelegation): boolean {
  return (
    (ctx.me.person?.id !== undefined && delegation.personId === ctx.me.person.id) ||
    delegation.userId === ctx.me.user.id
  );
}

function toMine(ctx: DelegationRouteContext, delegation: FakeDelegation) {
  const study = ctx.state.studies.find((item) => item.id === delegation.studySiteId);
  return {
    tenantId: delegation.tenantId,
    protocolCode: study?.protocolCode ?? null,
    studyTitle: study?.title ?? null,
    delegation: respond(ctx, delegation),
  };
}

function listMine(ctx: DelegationRouteContext, request: Request): Response {
  const status = new URL(request.url).searchParams.get('status');
  if (status !== null && status !== '' && !STATUSES.has(status)) {
    throw validation([fieldError('status', 'invalid')]);
  }
  const mine = ctx.state.delegations
    .filter((item) => isMine(ctx, item) && item.piSignature !== null)
    .filter((item) => !status || item.status === status)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt) || b.version - a.version);
  return ok(mine.map((item) => toMine(ctx, item)));
}

async function accept(
  ctx: DelegationRouteContext,
  request: Request,
  delegationId: string,
): Promise<Response> {
  requireStepUp(ctx);
  const delegation = ctx.state.delegations.find((item) => item.id === delegationId);
  // Uma delegação que não é dela (ou que o PI ainda não assinou) não existe para ela (404).
  if (!delegation || !isMine(ctx, delegation) || delegation.piSignature === null) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  const study = ctx.state.studies.find((item) => item.id === delegation.studySiteId);
  if (!study) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  // SoD (delegation.sign ≠ delegation.accept): quem assinou como PI não aceita a mesma delegação.
  if (delegation.piSignature.signedBy === ctx.me.user.id) {
    throw new FakeApiError(422, 'AUTHZ_SOD_VIOLATION');
  }
  const body = await readJsonBody(request);
  const hash = signedHash(body);
  if (delegation.status !== 'PendingAcceptance') {
    throw new FakeApiError(409, 'AUTHZ_DELEGATION_NOT_PENDING_ACCEPTANCE', {
      detail: delegation.status,
    });
  }
  requireOpen(study);
  periodEnded(delegation, todayUtc());
  if (hash.toLowerCase() !== delegation.contentHash.toLowerCase()) {
    throw new FakeApiError(409, 'AUTHZ_DELEGATION_CONTENT_CHANGED');
  }
  ensureAllowed(
    ctx,
    study,
    'Accept',
    {
      personId: delegation.personId,
      functionCode: delegation.functionCode,
      taskCodes: delegation.tasks.map((item) => item.taskCode),
      effectiveFrom: delegation.effectiveFrom,
      effectiveTo: delegation.effectiveTo,
      delegationId: delegation.id,
    },
    ctx.me.user.id,
  );
  const now = new Date().toISOString();
  delegation.status = 'Active';
  delegation.userId = ctx.me.user.id;
  delegation.acceptance = {
    signedBy: ctx.me.user.id,
    signerPersonId: ctx.me.person?.id ?? delegation.personId,
    signedAt: now,
    meaning: 'Accept',
    comment: str(body, 'comment'),
  };
  delegation.activatedAt = now;
  delegation.roleAssignmentId = delegation.studyRoleCode ? fakeDelegationId(ctx.state) : null;
  touch(delegation);
  // A emenda aceita substitui a versão anterior na mesma transação, sem lacuna de acesso.
  if (delegation.previousDelegationId) {
    const previous = ctx.state.delegations.find(
      (item) => item.id === delegation.previousDelegationId,
    );
    if (previous && CURRENT_STATUSES.has(previous.status)) {
      previous.status = 'Superseded';
      previous.endedAt = now;
      previous.endedBy = ctx.me.user.id;
      touch(previous);
    }
  }
  const log = logOf(ctx, study.id);
  if (log) {
    log.currentVersion += 1;
  }
  grantStudyRole(ctx, delegation);
  audit(ctx, 'AUTHZ.DELEGATION_ACCEPTED', delegation, { contentHash: delegation.contentHash });
  audit(ctx, 'AUTHZ.DELEGATION_ACTIVATED', delegation);
  return ok(toMine(ctx, delegation), delegationEtag(delegation));
}

// ---- Ponto de entrada ----

/** Trata as rotas do delegation log e `/me/delegations*`; `null` para qualquer outro caminho. */
export async function handleDelegationRoute(
  request: Request,
  segments: readonly string[],
  ctx: DelegationRouteContext,
): Promise<Response | null> {
  try {
    if (segments[0] === 'me' && segments[1] === 'delegations') {
      const rest = segments.slice(2);
      if (request.method === 'GET' && rest.length === 0) {
        return listMine(ctx, request);
      }
      const acceptParams = match(rest, [':delegationId', 'accept']);
      if (request.method === 'POST' && acceptParams) {
        return await accept(ctx, request, acceptParams['delegationId'] ?? '');
      }
      return null;
    }
    if (
      segments[0] !== 'tenants' ||
      !segments[1] ||
      segments[2] !== 'studies' ||
      !segments[3] ||
      segments[4] !== 'delegation-log'
    ) {
      return null;
    }
    const tenantId = segments[1];
    const studySiteId = segments[3];
    const rest = segments.slice(5);
    const { method } = request;
    if (method === 'GET') {
      if (rest.length === 0) {
        return getLog(ctx, tenantId, studySiteId);
      }
      if (match(rest, ['task-types'])) {
        return getTaskTypes(ctx, tenantId, studySiteId);
      }
      if (match(rest, ['block-attempts'])) {
        return getAttempts(ctx, tenantId, studySiteId);
      }
      if (match(rest, ['export.pdf'])) {
        return exportPdf(ctx, request, tenantId, studySiteId);
      }
      const one = match(rest, ['delegations', ':delegationId']);
      if (one) {
        return getDelegation(ctx, tenantId, studySiteId, one['delegationId'] ?? '');
      }
      return null;
    }
    if (method === 'POST') {
      if (match(rest, ['delegations'])) {
        return await createDraft(ctx, request, tenantId, studySiteId);
      }
      const action = match(rest, ['delegations', ':delegationId', ':action']);
      if (action) {
        const id = action['delegationId'] ?? '';
        switch (action['action']) {
          case 'pi-sign':
            return await piSign(ctx, request, tenantId, studySiteId, id);
          case 'revoke':
            return await revoke(ctx, request, tenantId, studySiteId, id);
          case 'amend':
            return await amend(ctx, request, tenantId, studySiteId, id);
          default:
            return null;
        }
      }
    }
    return null;
  } catch (error: unknown) {
    if (error instanceof FakeApiError) {
      return problemResponse(error.status, error.errorCode, ctx.correlationId, error.extra);
    }
    throw error;
  }
}
